import { CountryCode } from "@features/country/domain";
import {
  ChallengeOrderType,
  ChallengeStatus,
  type DynamicIncentiveChallengeEntity,
  type HistoryDynamicIncentiveProgramEntity,
} from "@features/dynamic-incentives/domain";
import type {
  DynamicIncentiveChallengeModel,
  HistoryDynamicIncentiveProgramModel,
} from "../models";

/**
 * Map state-related properties
 */
function mapStateProperties(
  model: DynamicIncentiveChallengeModel,
  now: Date,
  endDate?: Date,
): DynamicIncentiveChallengeEntity["state"] {
  const isOffered = model.status === ChallengeStatus.OFFERED;
  const isInProgress =
    model.status === ChallengeStatus.IN_PROGRESS ||
    model.status === ChallengeStatus.IN_PROGRESS_EXTRA_TIME;
  const isCompleted =
    model.status === ChallengeStatus.ACHIEVED ||
    model.status === ChallengeStatus.NOT_ACHIEVED;
  const isAchieved = model.status === ChallengeStatus.ACHIEVED;
  const isNotAchieved = model.status === ChallengeStatus.NOT_ACHIEVED;
  const isExpired = model.expiryDate ? new Date(model.expiryDate) < now : false;

  const challengeTimeIsUp = endDate ? endDate < now : false;
  const rewardsNotEqualToBaseRewards =
    model.challengeResult?.baseRewards !== model.challengeResult?.rewards;
  const challengeInProgress = model.status === ChallengeStatus.IN_PROGRESS;
  const challengeStillHasTimeToEnd = endDate ? endDate > now : false;

  const hasBaseRewards = model.challengeResult?.baseRewards !== undefined;

  const shouldDisplayProgressBar = model.status !== ChallengeStatus.ACHIEVED;

  const canSubmitFeedback = [
    ChallengeStatus.NOT_ACHIEVED,
    ChallengeStatus.ACHIEVED,
    ChallengeStatus.IN_PROGRESS_EXTRA_TIME,
  ].includes(model.status);

  return {
    isOffered,
    isInProgress,
    isCompleted,
    isAchieved,
    isNotAchieved,
    isExpired,
    challengeTimeIsUp,
    shouldDisplayProgressBar,
    rewardsNotEqualToBaseRewards,
    challengeInProgress,
    challengeStillHasTimeToEnd,
    hasBaseRewards,
    canSubmitFeedback,
  };
}

/**
 * Map progress-related properties
 */
function mapProgressProperties(
  model: DynamicIncentiveChallengeModel,
): DynamicIncentiveChallengeEntity["progress"] {
  // Calculate progress
  let progressValue = 0;
  const targetValue = model.levels[0]?.numOfOrders || model.maxNumberOfOrders;

  switch (model.orderType) {
    case ChallengeOrderType.CONFIRMED:
      progressValue = model.ordersProgressResult?.confirmedOrders || 0;
      break;
    case ChallengeOrderType.DELIVERED:
      progressValue = model.ordersProgressResult?.deliveredOrders || 0;
      break;
    case ChallengeOrderType.PREPAID:
      progressValue = model.ordersProgressResult?.prePaidOrdersCount || 0;
      break;
    case ChallengeOrderType.RECEIVED:
    default:
      progressValue = model.ordersProgressResult?.totalPlacedOrders || 0;
      break;
  }

  const progressPercentage =
    targetValue > 0 ? Math.min(100, (progressValue / targetValue) * 100) : 0;

  // Calculate loading progress
  let loadingProgress = 0;
  const ordersPerLevel = model.levels[0]?.numOfOrders || 0;
  const confirmedOrders = model.ordersProgressResult?.confirmedOrders || 0;
  const deliveredOrders = model.ordersProgressResult?.deliveredOrders || 0;
  const receivedOrders = model.ordersProgressResult?.totalPlacedOrders || 0;
  const prepaidOrders = model.ordersProgressResult?.prePaidOrdersCount || 0;

  if (model.orderType === ChallengeOrderType.CONFIRMED) {
    loadingProgress = (confirmedOrders / ordersPerLevel) * 100;
  } else if (model.orderType === ChallengeOrderType.DELIVERED) {
    loadingProgress = (deliveredOrders / ordersPerLevel) * 100;
  } else if (model.orderType === ChallengeOrderType.RECEIVED) {
    loadingProgress = (receivedOrders / ordersPerLevel) * 100;
  } else if (model.orderType === ChallengeOrderType.PREPAID) {
    loadingProgress = (prepaidOrders / ordersPerLevel) * 100;
  }

  // Calculate number of additional orders and total rewards for extra orders
  const numberOfAdditionalOrders =
    model.challengeResult?.additionalAchievedOrders || 0;
  const totalRewardsForExtraOrders =
    numberOfAdditionalOrders *
    (model.levels[0]?.rewardPerOrderExceedingLevelCap || 0);

  return {
    percentage: progressPercentage,
    value: progressValue,
    target: targetValue,
    loading: loadingProgress,
    numberOfAdditionalOrders,
    totalRewardsForExtraOrders,
  };
}

/**
 * Map time-related properties
 */
function mapTimeProperties(
  model: DynamicIncentiveChallengeModel,
  now: Date,
  challengeTimeIsUp: boolean,
  endDate?: Date,
  extraTime?: Date,
): DynamicIncentiveChallengeEntity["time"] {
  // Calculate extra time in days
  const extraTimeInDays = extraTime
    ? Math.round(
        (extraTime.getTime() - endDate!.getTime()) / (1000 * 60 * 60 * 24),
      )
    : 0;

  return {
    durationInDays: model.durationInHours / 24,
    durationInHours: model.durationInHours,
    remainingInHours:
      model.status === ChallengeStatus.IN_PROGRESS && endDate
        ? Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60))
        : 0,
    isUp: challengeTimeIsUp,
    hasRemaining: model.status === ChallengeStatus.IN_PROGRESS,
    extraTimeInDays,
    acceptedDate: model.challengeResult?.createdAt || undefined,
  };
}

/**
 * Map rewards-related properties
 */
function mapRewardsProperties(
  model: DynamicIncentiveChallengeModel,
): DynamicIncentiveChallengeEntity["rewards"] {
  // Calculate reward values
  const baseRewards = model.challengeResult?.baseRewards || 0;
  const additionalRewards = (model.challengeResult?.rewards || 0) - baseRewards;

  return {
    hasAdditional: additionalRewards > 0,
    hasStandard: baseRewards > 0,
    hasWon: model.status === ChallengeStatus.ACHIEVED,
    max: model.maxRewards,
    base: baseRewards,
    additional: additionalRewards,
    difference:
      (model.challengeResult?.rewards || 0) -
      (model.challengeResult?.baseRewards || 0),
  };
}

/**
 * Map carnival-related properties
 */
function mapCarnivalProperties(
  model: DynamicIncentiveChallengeModel,
  challengeStillHasTimeToEnd: boolean,
): DynamicIncentiveChallengeEntity["carnival"] {
  const exists = (model.levels[0]?.rewardPerOrderExceedingLevelCap || 0) > 0;
  const hasWon = model.status === ChallengeStatus.ACHIEVED;
  const hasAdditionalOrders =
    (model.challengeResult?.additionalAchievedOrders ?? 0) > 0;
  const didNotWin =
    !challengeStillHasTimeToEnd &&
    [
      ChallengeStatus.NOT_ACHIEVED,
      ChallengeStatus.IN_PROGRESS_EXTRA_TIME,
      ChallengeStatus.ACHIEVED,
    ].includes(model.status) &&
    model.challengeResult?.baseRewards === model.challengeResult?.rewards;

  const inProgress =
    exists && challengeStillHasTimeToEnd && hasAdditionalOrders;

  const shouldDisplayDetails = exists && hasAdditionalOrders;

  return {
    exists,
    hasWon,
    shouldDisplayDetails,
    didNotWin,
    inProgress,
    achievedOrders: model.challengeResult?.additionalAchievedOrders || 0,
    rewardPerExtraOrder: model.levels[0]?.rewardPerOrderExceedingLevelCap || 0,
  };
}

/**
 * Map title-related properties based on challenge status
 */
function mapTitle(
  model: DynamicIncentiveChallengeModel,
): DynamicIncentiveChallengeEntity["title"] {
  if (model.status === ChallengeStatus.ACHIEVED) {
    return "incentives.challengeTitle.achieved";
  }

  if (model.status === ChallengeStatus.NOT_ACHIEVED) {
    return "incentives.challengeTitle.notAchieved";
  }

  return "incentives.challengeTitle.default";
}

/**
 * Map a single dynamic incentive challenge model to entity
 */
export function mapDynamicIncentiveChallenge(
  model: DynamicIncentiveChallengeModel,
): DynamicIncentiveChallengeEntity {
  const now = new Date();
  const startDate = model.startDate ? new Date(model.startDate) : undefined;
  const expiryDate = model.expiryDate ? new Date(model.expiryDate) : undefined;
  const extraTime = model.extraTime ? new Date(model.extraTime) : undefined;
  let endDate = model.endDate ? new Date(model.endDate) : undefined;

  if (!endDate && model.durationInHours) {
    endDate = new Date(
      (startDate || now).getTime() + model.durationInHours * 60 * 60 * 1000,
    );
  }

  // Calculate challenge time is up flag
  const challengeTimeIsUp = endDate ? endDate < now : false;
  const challengeStillHasTimeToEnd = endDate ? endDate > now : false;

  // Map properties using the specialized mappers
  const state = mapStateProperties(model, now, endDate);
  const progress = mapProgressProperties(model);
  const time = mapTimeProperties(
    model,
    now,
    challengeTimeIsUp,
    endDate,
    extraTime,
  );
  const rewards = mapRewardsProperties(model);
  const carnival = mapCarnivalProperties(model, challengeStillHasTimeToEnd);
  const title = mapTitle(model);

  return {
    name: model.name,
    result: model.challengeResult
      ? {
          ...model.challengeResult,
          createdAt: new Date(model.challengeResult.createdAt),
        }
      : undefined,
    country: model.country as CountryCode,
    description: model.description,
    termsAndConditions: model.termsAndConditions,
    levels: model.levels,
    type: model.type,
    status: model.status,
    startDate,
    endDate,
    expiryDate,
    extraTime,
    id: model.merchantChallengeBusinessId,
    bucketType: model.bucketType,
    sku: model.sku,
    productId: model.productId,
    orders: {
      count: model.ordersCount,
      max: model.maxNumberOfOrders,
      type: model.orderType,
      progressResult: model.ordersProgressResult,
      numberOfOrders: model.levels[0]?.numOfOrders || 0,
    },
    state,
    progress,
    time,
    rewards,
    carnival,
    title,
  };
}

/**
 * Map a history dynamic incentive program model to entity
 */
export function mapHistoryDynamicIncentiveProgram(
  model: HistoryDynamicIncentiveProgramModel,
): HistoryDynamicIncentiveProgramEntity {
  return {
    ...model,
    startedAt: new Date(model.startedAt),
    endedAt: new Date(model.endedAt),
  };
}
